import React, { useState } from 'react';
import { Quest, QuestType } from '../types';
import QuestCard from './QuestCard';
import { useLanguage } from '../contexts/LanguageContext';

interface QuestListProps {
  quests: Quest[];
  onComplete: (questId: string) => void;
  onDelete: (questId: string, silent?: boolean, deleteSeries?: boolean) => void;
  onUpdate?: (questId: string, updates: Partial<Quest>) => void;
  onToggleSubTask?: (questId: string, subTaskId: string) => void;
}

const QuestList: React.FC<QuestListProps> = ({ quests, onComplete, onDelete, onUpdate, onToggleSubTask }) => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState<'all' | QuestType | 'completed' | 'scheduled'>('all');

  // Get today's date for filtering scheduled quests
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const filteredQuests = quests
    .filter((quest) => {
      if (filter === 'all') return quest.status === 'active' && (!quest.scheduledFor || quest.scheduledFor <= todayStr);
      if (filter === 'completed') return quest.status === 'completed';
      if (filter === 'scheduled') return quest.scheduledFor && quest.scheduledFor > todayStr && quest.status !== 'completed';
      return quest.type === filter && quest.status === 'active' && (!quest.scheduledFor || quest.scheduledFor <= todayStr);
    })
    .sort((a, b) => {
      // Sort scheduled quests by scheduledFor date (earliest to latest)
      if (filter === 'scheduled' && a.scheduledFor && b.scheduledFor) {
        return a.scheduledFor.localeCompare(b.scheduledFor);
      }
      
      // For active quests, prioritize those with scheduledFor dates and sort them
      if (filter === 'all' || filter !== 'completed') {
        if (a.scheduledFor && b.scheduledFor) {
          return a.scheduledFor.localeCompare(b.scheduledFor);
        }
        if (a.scheduledFor && !b.scheduledFor) return -1; // Scheduled quests first
        if (!a.scheduledFor && b.scheduledFor) return 1;
      }
      
      // For completed quests, sort by completion date (most recent first)
      if (filter === 'completed' && a.completedAt && b.completedAt) {
        return b.completedAt - a.completedAt;
      }
      
      // Default: sort by creation date (most recent first)
      return b.createdAt - a.createdAt;
    });

  const activeCount = quests.filter(q => q.status === 'active' && (!q.scheduledFor || q.scheduledFor <= todayStr)).length;
  const completedCount = quests.filter(q => q.status === 'completed').length;
  const scheduledCount = quests.filter(q => q.scheduledFor && q.scheduledFor > todayStr && q.status !== 'completed').length;

  const filterOptions = [
    { value: 'all', label: t.dashboard.activeQuests, iconPath: null }, // Use gradient background
    { value: 'daily', label: t.quests.daily, iconPath: '/icons/icon-quest-daily.png' },
    { value: 'main', label: t.quests.main, iconPath: '/icons/icon-quest-main.png' },
    { value: 'instant', label: 'Instant', iconPath: '/icons/icon-quest-instant.png' },
    { value: 'boss', label: t.quests.boss, iconPath: '/icons/icon-quest-boss.png' },
    { value: 'scheduled', label: t.dashboard.scheduledQuests, iconPath: '/icons/icon-quest-scheduled.png' },
    { value: 'completed', label: t.common.completed, iconPath: '/icons/icon-quest-completed.png' },
  ];

  return (
    <div 
      className="relative overflow-hidden bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl"
      style={{
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
      }}
    >
      <div className="p-3 sm:p-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-5">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-gradient-to-br from-system-blue to-blue-400 rounded-full animate-pulse shadow-lg shadow-system-blue/50"></div>
            <h2 className="text-lg sm:text-xl font-bold font-orbitron tracking-wider text-white uppercase">
              {t.nav.quests}
            </h2>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm font-rajdhani flex-wrap">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-system-blue rounded-full"></div>
              <span className="text-gray-400">{t.dashboard.activeQuests}:</span>
              <span className="text-white font-bold">{activeCount}</span>
            </div>
            {scheduledCount > 0 && (
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                <span className="text-gray-400">{t.dashboard.scheduledQuests}:</span>
                <span className="text-white font-bold">{scheduledCount}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
              <span className="text-gray-400">Done:</span>
              <span className="text-white font-bold">{completedCount}</span>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-1.5 sm:gap-2 mb-4 sm:mb-5 flex-wrap">
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value as any)}
              className={`relative px-3 sm:px-4 py-1.5 sm:py-2 rounded-2xl font-rajdhani font-semibold text-xs tracking-wide transition-all ${
                filter === option.value
                  ? 'bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 border-t-2 border-l-2 border-blue-500/40 border-r-2 border-b-2 border-r-blue-700/60 border-b-blue-700/60 text-white scale-105'
                  : 'bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 text-gray-400 hover:text-white hover:border-blue-500/50'
              }`}
              style={filter === option.value ? {
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), 0 1px 4px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(100, 150, 255, 0.15), inset 0 -1px 3px rgba(30, 58, 138, 0.4)'
              } : {
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.06), inset 0 -1px 2px rgba(0, 0, 0, 0.3)'
              }}
            >
              <span className="flex items-center gap-1.5 sm:gap-2 relative z-10">
                {option.iconPath ? (
                  <img 
                    src={option.iconPath} 
                    alt={option.label} 
                    className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                    style={{ filter: filter === option.value ? 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.8))' : 'drop-shadow(0 0 2px rgba(59, 130, 246, 0.3))' }}
                  />
                ) : (
                  <span className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-system-blue to-system-purple rounded-full"></span>
                )}
                <span className="uppercase text-xs">{option.label}</span>
              </span>
              {filter === option.value && (
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl pointer-events-none"></div>
              )}
            </button>
          ))}
        </div>

        {/* Quest List */}
        <div className="space-y-3 sm:space-y-4 max-h-[400px] sm:max-h-[500px] overflow-y-auto pr-1 sm:pr-2">
          {filteredQuests.length === 0 ? (
            <div 
              className="relative bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl p-6 sm:p-8 text-center"
              style={{
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.08), inset 0 -2px 6px rgba(0, 0, 0, 0.4)'
              }}
            >
              <div className="text-4xl sm:text-5xl mb-3 sm:mb-4 opacity-50">📭</div>
              <p className="text-gray-400 font-rajdhani text-sm sm:text-base">
                {filter === 'completed' 
                  ? t.history.noHistory
                  : t.dashboard.noQuestsDesc}
              </p>
            </div>
          ) : (
            filteredQuests.map((quest, index) => (
              <div
                key={quest.id}
                className="animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <QuestCard
                  quest={quest}
                  onComplete={onComplete}
                  onDelete={onDelete}
                  onUpdate={onUpdate}
                  onToggleSubTask={onToggleSubTask}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestList;
